import { RealtimeRole } from '@doxyme/realtime';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../../stores/redux';
import { track, TrackedEvents } from '../tracking';
import {
  type CallRatingInput,
  saveCallRating,
  type SaveCallRatingInput,
  sessionApi,
  subscriptions,
} from '../../services';
import { getCallDetailsForSurvey } from './postCallSurvey.selectors';
import { getApolloSessionDetails } from '../apolloSession/apolloSession.selectors';
import { getIsAnimationDisabled } from '../uiState/uiState.selectors';
import { getGPUData } from '../../shared/deviceCapabilities';
import { logger } from './logger';
import { session } from '../../webrtc/session';
import { getIsProvider } from '../realtime/realtime.selectors';
import { pubSubStatusStats } from '../../hooks/useTracking/pubSubStatusStats';
import { otExceptionsStats } from '../../hooks/useTracking/otExceptionsStats';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';

export type SurveyValues = {
  rating: number;
  problems: string[];
  comment: string;
};

export const submitPostCallSurvey = createAsyncThunk<
  void,
  SurveyValues,
  { state: RootState; dispatch: AppDispatch }
>('postCallSurvey/submit', async (values, { getState, dispatch }) => {
  const callDetails = getCallDetailsForSurvey(getState());
  if (!callDetails) {
    return;
  }

  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

  const { dbSessionId, sessionId, noSoundDetected, vendor } = callDetails;

  const isProvider = getIsProvider(getState());

  const problems = values.problems.map((problem) =>
    problem.replaceAll('_', ' '),
  );
  const comment = values.comment.trim();

  const payload: CallRatingInput = {
    sessionId: dbSessionId,
    channel: sessionId,
    rating: values.rating,
    data: problems,
    comment,
    noSoundDetected,
    isProviderFeedback: isProvider,
    surveyType: 'apollo',
    vendor,
  };

  const logToDataDog = (): void => {
    const rating = values.rating;

    const formattedProblems = [...problems];

    if (comment.length > 0) {
      formattedProblems.push(`otherissue/${comment}`);
    }

    // TODO: ideally, we match the problems to the original dokbot categorised values
    logger.info('callRated', {
      rating,
      problems: formattedProblems,
      metrics: session.getMetrics(),
      ...pubSubStatusStats.finalizeAndGetStats(),
      otExceptionsStats: otExceptionsStats.finalizeAndGetStats(),
    });
  };

  const saveToOGAPI = async (): Promise<void> => {
    const logProps = { callDetails, values };

    try {
      if (isGraphqlRoomQueriesEnabled) {
        const input: SaveCallRatingInput = {
          sessionId: dbSessionId,
          channel: sessionId,
          rating: values.rating,
          data: problems,
          comment,
          noSoundDetected,
        };

        if (isProvider) {
          await dispatch(
            sessionApi.endpoints.SaveCallRatingFromProvider.initiate({ input }),
          ).unwrap();
        } else {
          const { roomId } = getApolloSessionDetails(getState());
          await dispatch(
            sessionApi.endpoints.SaveCallRatingFromPatient.initiate({
              input: { ...input, ...(roomId && { roomId }) },
            }),
          ).unwrap();
        }
      } else {
        await dispatch(saveCallRating.initiate(payload)).unwrap();
      }

      logger.info('[postCallSurvey/submit]', logProps);
    } catch {
      logger.error('[postCallSurvey/submit]', logProps);
    }
  };

  const saveToSegment = async (): Promise<void> => {
    const { userId, roomId, role } = getApolloSessionDetails(getState());

    const { tier: gpuTier } = await getGPUData();
    const isAnimationDisabled = getIsAnimationDisabled(getState());

    let subscriptionType = undefined;

    if (Boolean(userId)) {
      const { data } = await dispatch(
        subscriptions.endpoints.getSubscriptionType.initiate({
          userId: String(userId),
        }),
      );
      subscriptionType = data?.subscriptionType;
    }

    void track(TrackedEvents.CallRated, {
      userType: role?.toLowerCase(),
      userId,
      planType: subscriptionType,
      domain: null,
      clinicId: null,
      roomId:
        typeof roomId !== 'undefined' ? Number.parseInt(roomId) : undefined,
      sessionId: dbSessionId,
      channel: sessionId,
      noSoundDetected,
      rating: values.rating,
      data: problems,
      comment,
      isProviderFeedback: role === RealtimeRole.Provider,
      surveyType: 'apollo',
      vendor,
      isApollo: true,
      gpuTier,
      isAnimationDisabled,
      ...pubSubStatusStats.finalizeAndGetStats(),
    });
  };

  void saveToOGAPI();
  logToDataDog();
  void saveToSegment();
});
